import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, Lock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import WishlistButton from './WishlistButton';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const isLocked = !!product.comingSoon;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLocked) return;
    addToCart(product);
  };

  // Calculate discount percentage
  const getDiscountPercent = () => {
    if (product.originalPrice && product.originalPrice > product.price) {
      return Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
    }
    return 0;
  };

  const discountPercent = getDiscountPercent();
  const formatRating = (rating) => Number(rating || 5).toFixed(2);

  // Locked products: render a non-link wrapper so clicking doesn't navigate
  const Wrapper = isLocked ? 'div' : Link;
  const wrapperProps = isLocked
    ? { 'data-testid': `product-card-${product.slug}`, 'aria-disabled': 'true' }
    : { to: `/product/${product.slug}`, 'data-testid': `product-card-${product.slug}` };

  return (
    <Wrapper {...wrapperProps}>
      <div
        className={`product-card-modern group relative ${isLocked ? 'opacity-90 cursor-not-allowed' : ''}`}
        data-testid="product-card"
      >
        {/* Image Container */}
        <div className="product-card-image relative">
          <img
            src={product.image}
            alt={product.name}
            className={`w-full h-full object-cover transition-all duration-500 ${
              isLocked ? 'grayscale-[40%] blur-[1px]' : 'group-hover:scale-105'
            }`}
          />

          {/* Coming Soon overlay — locked products */}
          {isLocked && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/55 backdrop-blur-[2px] text-white"
              data-testid={`coming-soon-${product.slug}`}
            >
              <Lock className="h-7 w-7 mb-2 opacity-90" />
              <span className="font-semibold text-sm tracking-wider uppercase">Coming Soon</span>
              <span className="text-xs opacity-80 mt-1">Get notified at launch</span>
            </div>
          )}

          {/* Wishlist Button - Top Left (still allowed on locked items so users can save) */}
          {!isLocked && (
            <div className="absolute top-3 left-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <WishlistButton product={product} size="small" />
            </div>
          )}

          {/* Discount Badge - Top Right */}
          {discountPercent > 0 && !isLocked && (
            <div className="product-discount-badge">-{discountPercent}%</div>
          )}

          {/* Product Badge Overlay (only when explicitly set in the manifest) */}
          {product.badge && !isLocked && (
            <div className="product-badge-overlay">{product.badge}</div>
          )}

          {/* Quick Add Overlay — only when available */}
          {!isLocked && (
            <div className="product-quick-add">
              <button
                onClick={handleAddToCart}
                className="quick-add-btn"
                data-testid={`add-to-cart-${product.slug}`}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Add to Cart
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="product-card-content">
          <div className="product-rating-row">
            <div className="product-rating">
              <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
              <span className="rating-value">{formatRating(product.rating)}</span>
            </div>
            {product.reviews > 0 && <span className="review-count">{product.reviews}</span>}
          </div>

          <h3 className="product-card-title">{product.name}</h3>

          <div className="product-price-row">
            {isLocked ? (
              <span className="current-price text-slate-400">—</span>
            ) : product.originalPrice && discountPercent > 0 ? (
              <>
                <span className="original-price">${product.originalPrice.toFixed(2)}</span>
                <span className="sale-price">${product.price.toFixed(2)}</span>
              </>
            ) : (
              <span className="current-price">${product.price.toFixed(2)}</span>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default ProductCard;
